import { useContext, useState, useEffect } from "react";
import { TeachersContext } from "../../contexts/teachers-context";
import { useAuth } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import createAxiosInstance from "../utility/utils";
import {
  Container,
  Breadcrumb,
  Card,
  Row,
  Col,
  Table,
  Button,
  Form,
  InputGroup,
  Spinner,
  Alert,
} from "react-bootstrap";
import { Search, PersonBadge, ArrowClockwise } from "react-bootstrap-icons";

const themeColor = "rgb(79, 81, 163)";

export default function ListTeachers() {
  const { teachers, teachersDispatch } = useContext(TeachersContext);
  const { getToken } = useAuth();
  const axiosInstance = createAxiosInstance(getToken);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [serverError, setServerError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setIsLoading(false);
      } catch (err) {
        setServerError("Error loading teachers list.");
        setIsLoading(false);
      }
    })();
  }, []);

  const handleRemove = async (id) => {
    const userConfirm = window.confirm(
      "Are you sure you want to delete this teacher? This action cannot be undone."
    );
    if (userConfirm) {
      setDeleteLoading(id);
      try {
        await axiosInstance.delete(`/api/users/${id}`);
        teachersDispatch({ type: "REMOVE_TEACHER", payload: id });
        setActionSuccess("Teacher deleted successfully");
        setTimeout(() => setActionSuccess(""), 3000);
      } catch (err) {
        if (err.response) {
          if (err.response.status === 403) {
            setServerError(
              "Access Denied: You don't have permission to remove a teacher."
            );
          } else {
            setServerError(
              err.response.data.error ||
                "An error occurred while deleting the teacher."
            );
          }
        } else if (err.request) {
          setServerError("No response from server. Please check your network.");
        } else {
          setServerError("Request error: " + err.message);
        }
      } finally {
        setDeleteLoading(null);
      }
    }
  };

  const handleViewSessions = (teacherId) => {
    navigate(`/admin/teacher/${teacherId}/sessions`);
  };

  const filteredTeachers = teachers?.data?.filter((teacher) => {
    const searchString = searchTerm.toLowerCase();
    return (
      teacher.name.toLowerCase().includes(searchString) ||
      teacher.employeeId.toLowerCase().includes(searchString) ||
      teacher.email.toLowerCase().includes(searchString)
    );
  });

  const clearSearch = () => {
    setSearchTerm("");
  };

  if (isLoading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "80vh" }}
      >
        <Card className="shadow p-5 text-center">
          <Spinner
            animation="border"
            style={{ color: themeColor }}
            className="mx-auto"
          />
          <h5 className="mt-3 text-muted">Loading Teachers...</h5>
          <p className="text-muted">Please wait while we fetch the data</p>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Breadcrumb className="mb-4">
        <Breadcrumb.Item href="/">Apps</Breadcrumb.Item>
        <Breadcrumb.Item active>Teachers List</Breadcrumb.Item>
      </Breadcrumb>

      {serverError && (
        <Alert variant="danger" onClose={() => setServerError("")} dismissible>
          {serverError}
        </Alert>
      )}

      {actionSuccess && (
        <Alert
          variant="success"
          onClose={() => setActionSuccess("")}
          dismissible
        >
          {actionSuccess}
        </Alert>
      )}

      <Card className="shadow mb-4">
        <Card.Header className="bg-white">
          <Row className="align-items-center">
            <Col
              xs={12}
              className="d-flex justify-content-between align-items-center flex-wrap"
            >
              <h4 className="mb-0 d-flex align-items-center">
                <PersonBadge className="me-2" style={{ color: themeColor }} />
                Teachers Directory
              </h4>
              <div className="mt-2 mt-md-0">
                <Button
                  style={{
                    backgroundColor: themeColor,
                    borderColor: themeColor,
                  }}
                  onClick={() => navigate("/admin-dashboard")}
                >
                  Add Teacher
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Form className="mb-4">
            <InputGroup>
              <InputGroup.Text>
                <Search />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by name, employee ID, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button variant="outline-secondary" onClick={clearSearch}>
                  Clear
                </Button>
              )}
            </InputGroup>
          </Form>

          <div className="table-responsive">
            <Table hover bordered className="align-middle">
              <thead className="bg-light">
                <tr>
                  <th className="text-center" style={{ width: "5%" }}>
                    #
                  </th>
                  <th style={{ width: "25%" }}>Teacher Name</th>
                  <th style={{ width: "20%" }}>Employee ID</th>
                  <th style={{ width: "30%" }}>Email</th>
                  <th className="text-center" style={{ width: "20%" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredTeachers?.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      <div className="text-muted">
                        <ArrowClockwise className="mb-2" size={24} />
                        <p>No teachers found matching your search criteria</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredTeachers?.map((teacher, i) => (
                    <tr key={teacher._id}>
                      <td className="text-center">{i + 1}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle d-flex justify-content-center align-items-center me-2"
                            style={{
                              width: "32px",
                              height: "32px",
                              fontSize: "12px",
                              color: "white",
                              backgroundColor: themeColor,
                            }}
                          >
                            {teacher.name
                              .split(" ")
                              .map((name) => name[0])
                              .join("")
                              .toUpperCase()}
                          </div>
                          <div>{teacher.name}</div>
                        </div>
                      </td>
                      <td>{teacher.employeeId}</td>
                      <td>{teacher.email}</td>
                      <td>
                        <div className="d-flex flex-column flex-sm-row justify-content-center gap-2">
                          <Button
                            size="sm"
                            style={{
                              borderColor: themeColor,
                              color: themeColor,
                            }}
                            variant="outline"
                            onClick={() => handleViewSessions(teacher._id)}
                          >
                            <i className="bi bi-calendar3 me-1"></i>
                            <span className="d-inline d-sm-none">view</span>
                            <span className="d-none d-sm-inline">
                              View Sessions
                            </span>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemove(teacher._id)}
                            disabled={deleteLoading === teacher._id}
                          >
                            {deleteLoading === teacher._id ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              "Delete"
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {filteredTeachers?.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <small className="text-muted">
                Showing {filteredTeachers.length} teachers
              </small>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
